import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Check, Trash2, Edit2, Layers } from 'lucide-react';

import { api } from '../../api/client.js';
import './SectorGroupsPanel.css';

/**
 * Sector groups: used by the matchmaker to score "related sector" matches.
 * Stored as settings.sector_groups = [{name, members: [...]}]
 */

export function SectorGroupsPanel() {
    const queryClient = useQueryClient();

    const { data: settings, isLoading, error } = useQuery({
        queryKey: ['settings'],
        queryFn: () => api.get('/settings'),
    });

    const groups = Array.isArray(settings?.sector_groups) ? settings.sector_groups : [];
    const availableSectors = Array.isArray(settings?.sectors) ? settings.sectors : [];

    const saveMutation = useMutation({
        mutationFn: (newGroups) => api.put('/settings/sector_groups', { value: newGroups }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
    });

    const [creating, setCreating] = useState(false);
    const [editingIdx, setEditingIdx] = useState(null);

    if (isLoading) return <div className="panel-empty"><span className="spinner" /></div>;
    if (error) return <div className="panel-error">Couldn't load sector groups: {error.message}</div>;

    const handleCreate = (group) => {
        saveMutation.mutate([...groups, group]);
        setCreating(false);
    };

    const handleUpdate = (idx, group) => {
        const next = [...groups];
        next[idx] = group;
        saveMutation.mutate(next);
        setEditingIdx(null);
    };

    const handleDelete = (idx) => {
        if (!confirm(`Delete group "${groups[idx].name}"? The matchmaker will no longer match these sectors as related.`)) return;
        saveMutation.mutate(groups.filter((_, i) => i !== idx));
    };

    return (
        <div>
            <div className="panel-section">
                <div className="panel-section-header">
                    <div>
                        <h2 className="panel-section-title">Sector groups</h2>
                        <p className="panel-section-subtitle">
                            Groups let the matchmaker recognize that "Clean energy" and "Climate tech" are related
                            sectors, even though they're spelled differently. Edit these to tune the matching behavior.
                        </p>
                    </div>
                    {!creating && (
                        <button className="btn-primary-small" onClick={() => setCreating(true)}>
                            <Plus size={14} strokeWidth={2}/> Add group
                        </button>
                    )}
                </div>

                {creating && (
                    <GroupForm
                        availableSectors={availableSectors}
                        existingGroupNames={groups.map(g => g.name)}
                        onSubmit={handleCreate}
                        onCancel={() => setCreating(false)}
                        saving={saveMutation.isPending}
                    />
                )}

                {groups.length === 0 && !creating && (
                    <div className="panel-empty">
                        <Layers size={28} strokeWidth={1.25} style={{ opacity: 0.4, marginBottom: 12 }} />
                        <p style={{ margin: 0 }}>
                            No sector groups yet. Without groups, matchmaker only finds exact sector matches.
                        </p>
                    </div>
                )}

                <ul className="group-list">
                    {groups.map((group, idx) => (
                        <li key={idx} className="group-card">
                            {editingIdx === idx ? (
                                <GroupForm
                                    initial={group}
                                    availableSectors={availableSectors}
                                    existingGroupNames={groups.filter((_, i) => i !== idx).map(g => g.name)}
                                    onSubmit={(g) => handleUpdate(idx, g)}
                                    onCancel={() => setEditingIdx(null)}
                                    saving={saveMutation.isPending}
                                />
                            ) : (
                                <>
                                    <div className="group-header">
                                        <div className="group-name">{group.name}</div>
                                        <div className="group-actions">
                                            <button className="user-action-btn" onClick={() => setEditingIdx(idx)}>
                                                <Edit2 size={12} strokeWidth={1.75}/> Edit
                                            </button>
                                            <button className="user-action-btn user-action-danger"
                                                    onClick={() => handleDelete(idx)}>
                                                <Trash2 size={12} strokeWidth={1.75}/>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="group-members">
                                        {group.members.map((m, i) => (
                                            <span key={i} className="group-member-pill">{m}</span>
                                        ))}
                                    </div>
                                </>
                            )}
                        </li>
                    ))}
                </ul>
            </div>

            <div className="panel-section panel-info">
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--color-ink-soft)' }}>
                    <strong>How scoring uses these groups:</strong> when a startup's sector doesn't exactly match an
                    investor's stated sector focus, the matchmaker checks if both are members of the same group. If yes,
                    the match gets 20 out of 30 sector points (exact match is 30). This rewards thematic overlap without
                    forcing exact string matches.
                </p>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
function GroupForm({ initial = {}, availableSectors, existingGroupNames, onSubmit, onCancel, saving }) {
    const [name, setName] = useState(initial.name || '');
    const [members, setMembers] = useState(initial.members || []);
    const [customMember, setCustomMember] = useState('');

    const nameError = name.trim() && existingGroupNames.includes(name.trim())
        ? `A group named "${name.trim()}" already exists.`
        : null;

    const toggleMember = (sector) => {
        setMembers(prev =>
            prev.includes(sector) ? prev.filter(m => m !== sector) : [...prev, sector]
        );
    };

    const addCustom = () => {
        const v = customMember.trim();
        if (!v || members.includes(v)) return;
        setMembers([...members, v]);
        setCustomMember('');
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!name.trim() || members.length === 0 || nameError) return;
        onSubmit({ name: name.trim(), members });
    };

    const extraMembers = members.filter(m => !availableSectors.includes(m));

    return (
        <form className="invite-form" onSubmit={handleSubmit} style={{ width: '100%' }}>
            <label className="detail-field">
                <span className="detail-field-label">Group name *</span>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Cleantech"
                    className="detail-field-input"
                    autoFocus
                />
                {nameError && <span className="field-error">{nameError}</span>}
            </label>

            <div className="detail-field">
                <span className="detail-field-label">
                    Members * &nbsp;<span style={{ color: 'var(--color-ink-soft)', fontWeight: 400 }}>
                        — pick from your sectors list, or type custom values
                    </span>
                </span>

                {availableSectors.length > 0 && (
                    <div className="startup-checkboxes" style={{ marginBottom: 'var(--space-2)' }}>
                        {availableSectors.map(s => (
                            <label key={s} className={`startup-checkbox${members.includes(s) ? ' is-checked' : ''}`}>
                                <input type="checkbox" checked={members.includes(s)}
                                       onChange={() => toggleMember(s)}/>
                                <span>{s}</span>
                            </label>
                        ))}
                    </div>
                )}

                <div className="dropdown-add">
                    <input
                        type="text"
                        value={customMember}
                        onChange={(e) => setCustomMember(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
                        placeholder="Add a custom sector name not in your list..."
                        className="dropdown-add-input"
                    />
                    <button type="button" className="dropdown-add-btn"
                            onClick={addCustom} disabled={!customMember.trim()}>
                        <Plus size={14} strokeWidth={2}/> Add
                    </button>
                </div>

                {extraMembers.length > 0 && (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                        <span className="detail-field-label" style={{ fontSize: 10 }}>Custom members</span>
                        <div className="group-members" style={{ marginTop: 4 }}>
                            {extraMembers.map((m, i) => (
                                <span key={i} className="group-member-pill group-member-custom">
                                    {m}
                                    <button type="button"
                                            onClick={() => setMembers(members.filter(x => x !== m))}
                                            className="group-member-remove">
                                        <X size={10} strokeWidth={2}/>
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="invite-actions">
                <button type="button" className="btn-ghost" onClick={onCancel} disabled={saving}>
                    <X size={14} strokeWidth={2}/> Cancel
                </button>
                <button type="submit" className="btn-save"
                        disabled={saving || !name.trim() || members.length === 0 || !!nameError}>
                    {saving ? <span className="spinner"/> : <><Check size={14} strokeWidth={2}/> Save group</>}
                </button>
            </div>
        </form>
    );
}
